import {
  Select,
  MenuItem,
  FormControl,
  Box,
  Typography,
  Button,
  CircularProgress,
  Stack,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Line, Bar, Scatter } from "react-chartjs-2";
import "chartjs-adapter-date-fns";
import "chart.js/auto";

import blueColors from "../blueColors";

const chartTypes = {
  line: Line,
  bar: Bar,
  scatter: Scatter,
};
// BORRAR DESPUÉS DE LA DEMO
const M3_TO_BARREL = 6.2898;

const states = [
  { code: 0, label: "Detected" },
  { code: 1, label: "Confirmed" },
  { code: 2, label: "Controlled" },
  { code: 3, label: "Escalating" },
  { code: 4, label: "Remediated" },
];

const MetricsChart = ({ data, onSelectedStatus }) => {
  const [chartData, setChartData] = useState(null);
  const [chartType] = useState("bar");
  const [selectedMetric, setSelectedMetric] = useState("total_leaks_per_state");
  const [isLoading, setIsLoading] = useState(true);

  const getChartData = (selectedMetric) => {
    if (!data || !data[selectedMetric] || data[selectedMetric].length === 0) {
      return null;
    }

    switch (selectedMetric) {
      case "total_leaks_per_state":
        const stateCounts = {};
        data[selectedMetric].forEach((item) => {
          stateCounts[item.state] = item.count;
        });

        return {
          labels: states.map((state) => state.label),
          datasets: [
            {
              label: "",
              data: states.map((state) => stateCounts[state.label] || 0),
              backgroundColor: blueColors.slice(0, states.length),
              borderColor: blueColors.slice(0, states.length),
              fill: true,
              tension: 0.1,
            },
          ],
        };
      case "total_volume_per_leak":
        return {
          labels: data[selectedMetric].map((item) => item.leak__identifier),
          datasets: [
            {
              label: "",
              data: data[selectedMetric].map((item) =>
                (item.total_volume * M3_TO_BARREL).toFixed(1)
              ),
              backgroundColor: blueColors,
              borderColor: blueColors,
              fill: true,
              tension: 0.1,
            },
          ],
        };
      case "total_area_per_leak":
        return {
          labels: data[selectedMetric].map((item) => item.leak__identifier),
          datasets: [
            {
              label: "",
              data: data[selectedMetric].map((item) => item.total_area),
              backgroundColor: blueColors,
              borderColor: blueColors,
              fill: true,
              tension: 0.1,
            },
          ],
        };
      default:
        return null;
    }
  };
  const getYAxis = (metric) => {
  switch (metric) {
    case "total_leaks_per_state":
      return {
        title: {
          display: true,
          text: "Number of leaks",
        },
        ticks: {
          stepSize: 1,
          precision: 0,
          callback: (value) => Number.isInteger(value) ? value : null,
        },
        beginAtZero: true,
      };

    case "total_volume_per_leak":
      return {
        title: {
          display: true,
          text: "Amount (barrels)",
        },
        beginAtZero: true,
      };

    case "total_area_per_leak":
      return {
        title: {
          display: true,
          text: "Area (m²)",
        },
        beginAtZero: true,
      };

    default:
      return {
        title: {
          display: true,
          text: "",
        },
        beginAtZero: true,
      };
    }
  };

  const getXAxis = (metric) => {
    switch (metric) {
      case "total_leaks_per_state":
        return {
          title: {
            display: true,
            text: "State",
          },
        };

      case "total_volume_per_leak":
        return {
          title: {
            display: true,
            text: "Leak",
          },
        };

      case "total_area_per_leak":
        return {
          title: {
            display: true,
            text: "Leak",
          },
        };

      default:
        return {
          title: {
            display: true,
            text: "",
          },
        };
    }
  };

  useEffect(() => {
    setIsLoading(true);
    const chartData = getChartData(selectedMetric);
    setChartData(chartData);
    setIsLoading(false);
  }, [data, selectedMetric]);

  const ChartComponent = chartTypes[chartType] || Line;

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    scales: {
      x: getXAxis(selectedMetric),
      y: getYAxis(selectedMetric)
    },
    plugins: {
      legend: {
        display: false,
      },
    },
    onClick: (_, elements) => {
      if (elements.length) {
        let filter;
        switch (selectedMetric) {
          case "total_leaks_per_state":
            filter = elements[0].index;
            onSelectedStatus(selectedMetric, filter);
            return;
          case "total_volume_per_leak":
            filter = data[selectedMetric][elements[0].index].leak__identifier;
            onSelectedStatus(selectedMetric, filter);
            return;
          case "total_area_per_leak":
            filter = data[selectedMetric][elements[0].index].leak__identifier;
            onSelectedStatus(selectedMetric, filter);
            return;
          default:
            return null;
        }
      }
    },
  };

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
      {/* Contenedor del selector */}
      <Box
        sx={{
          padding: 1,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          flexWrap: "wrap",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ width: "100%", justifyContent: "center", alignItems: "center" }}
        >
          {/* Menú desplegable */}
          <FormControl variant="standard" size="small" sx={{ minWidth: 120 }}>
            <Select
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              displayEmpty
              inputProps={{ "aria-label": "Without label" }}
              sx={{
                "&:before": { borderColor: "transparent" },
                "&:after": { borderColor: "transparent" },
                "& .MuiSelect-select": { paddingY: "5px" },
              }}
            >
              <MenuItem value="total_leaks_per_state">
                Total leaks per state
              </MenuItem>
              <MenuItem value="total_volume_per_leak">
                Total volume per leak
              </MenuItem>
              <MenuItem value="total_area_per_leak">
                Total area per leak
              </MenuItem>
            </Select>
          </FormControl>

          {/* Botón */}
          <Button
            onClick={() => onSelectedStatus("default", null)}
            variant="contained"
          >
            Show all
          </Button>
        </Stack>
      </Box>

      {/* Contenedor de la gráfica */}
      <Box
        width={"100%"}
        flexGrow={"1"}
        minHeight={"30vh"}
        maxHeight={"30vh"}
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
      >
        {isLoading ? (
          <CircularProgress />
        ) : chartData ? (
          <ChartComponent
            data={chartData}
            options={options}
            style={{ width: "100%", height: "100%" }}
          />
        ) : (
          <Typography variant="h6">
            No data available for the selected metric
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default MetricsChart;
